// keepAliveService.js
import axios from "axios"
import { Capacitor } from "@capacitor/core"
import { getStoredItem, setStoredItem } from "@/services/utils"

// Storage keys
const KEEP_ALIVE_KEY = "transportPingTimestamp"
const CONTACT_PING_KEY = "contactPingTimestamp"

// Render free tier idles ~50s -> ping < 50s
const PING_INTERVAL_MS = 45 * 1000 // send a real ping every 45s
const CHECK_EVERY_MS = 15 * 1000 // evaluate every 15s

// Endpoints
const TRANSPORT_PING_URL = "https://pinestack-mobility.onrender.com/ping"
const TRANSPORT_HEALTH_URL = "https://pinestack-mobility.onrender.com/health"
// Contact: /ping may 404; go straight to /health for reliability
const CONTACT_HEALTH_URL = "https://contact-4y9d.onrender.com/health"

// Axios client with sane defaults
const http = axios.create({
  timeout: 5000,
  headers: {
    "Cache-Control": "no-store",
    Pragma: "no-cache"
  },
  // Avoid cached responses in some browsers/CDNs
  paramsSerializer: params => {
    const usp = new URLSearchParams(params)
    // add timestamp to bust caches
    usp.append("_t", Date.now().toString())
    return usp.toString()
  }
})

let _transportTimer = null
let _contactTimer = null

function isScreenActive() {
  // Native apps are considered active
  if (Capacitor.isNativePlatform()) return true
  // On web, only when the tab is visible
  return typeof document !== "undefined" && document.visibilityState === "visible"
}

async function shouldPing(key) {
  const last = await getStoredItem(key)
  const now = Date.now()
  return !last || now - parseInt(last, 10) > PING_INTERVAL_MS
}

async function tryRequest(urls) {
  for (const url of urls) {
    try {
      const res = await http.get(url, { params: {} })
      if (res.status >= 200 && res.status < 300) {
        return { ok: true, url, status: res.status }
      }
      // Non-2xx: treat as failure, but continue to next fallback
    } catch (e) {
      // Network/timeout -> try next
      continue
    }
  }
  return { ok: false, url: urls[urls.length - 1], status: "ERR" }
}

function startAgentLoop({ name, storageKey, urls }) {
  const doPing = async () => {
    if (!isScreenActive()) return
    if (!(await shouldPing(storageKey))) return

    const result = await tryRequest(urls)
    if (result.ok) {
      await setStoredItem(storageKey, Date.now().toString())
      console.log(`🟢 ${name}: Ping successful (${result.url})`)
    } else {
      console.warn(`🔴 ${name}: Ping failed (${result.url})`, result.status)
    }
  }

  // Start periodic loop
  doPing()
  return setInterval(doPing, CHECK_EVERY_MS)
}

/**
 * Start both keep-alives. Contact is warmed up FIRST, immediately.
 * @param {boolean} immediateWarmup - if true, fires instant wake-up calls ignoring throttle/visibility
 */
export async function startKeepAlives(immediateWarmup = true) {
  // Clear any existing loops
  if (_transportTimer) clearInterval(_transportTimer)
  if (_contactTimer) clearInterval(_contactTimer)

  // --- Immediate warmup (Contact first) ---
  if (immediateWarmup) {
    try {
      const resC = await tryRequest([CONTACT_HEALTH_URL])
      if (resC.ok) {
        await setStoredItem(CONTACT_PING_KEY, Date.now().toString())
        console.log(`🟢 keepAliveContactAgent (warmup): ${resC.url}`)
      } else {
        console.warn(`🔴 keepAliveContactAgent (warmup) failed:`, resC.status)
      }
    } catch (e) {
      console.warn(`🔴 keepAliveContactAgent (warmup) error:`, e?.message || e)
    }

    try {
      const resT = await tryRequest([TRANSPORT_PING_URL, TRANSPORT_HEALTH_URL])
      if (resT.ok) {
        await setStoredItem(KEEP_ALIVE_KEY, Date.now().toString())
        console.log(`🟢 keepAliveTransportAgent (warmup): ${resT.url}`)
      } else {
        console.warn(`🔴 keepAliveTransportAgent (warmup) failed:`, resT.status)
      }
    } catch (e) {
      console.warn(`🔴 keepAliveTransportAgent (warmup) error:`, e?.message || e)
    }
  }

  // --- Start throttled periodic loops (every 15s check, ping every 45s) ---
  _contactTimer = startAgentLoop({
    name: "keepAliveContactAgent",
    storageKey: CONTACT_PING_KEY,
    urls: [CONTACT_HEALTH_URL]
  })

  _transportTimer = startAgentLoop({
    name: "keepAliveTransportAgent",
    storageKey: KEEP_ALIVE_KEY,
    urls: [TRANSPORT_PING_URL, TRANSPORT_HEALTH_URL]
  })
}

export function stopKeepAlives() {
  if (_transportTimer) clearInterval(_transportTimer)
  if (_contactTimer) clearInterval(_contactTimer)
  _transportTimer = null
  _contactTimer = null
}
